
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { getOrderScheduledDate, getOrderDelivery, getOrderType, getOrderStatus } from '@/utils/getters';
import TableHeaders from '@/components/shared/suites/TableHeaders.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import PaginationControls from '@/components/shared/suites/PaginationControls.vue';

@Component({
	components: {
		TableHeaders,
		TextButton,
		PaginationControls
	}
})
export default class OrderManagementTableList extends Vue {
	@Prop({ type: Array, required: true }) private orders!: Order[];
	@Prop({ type: Object, required: true }) private tableFilters!: { selectedGrades: string[]; selectedClasses: string[]; selectedTeachers: string[]; selectedStatuses: string[]; };
	@Prop({ type: Object, default: () => ({ pending: 0, inProgress: 0, completed: 0 }) }) private statusTotals!: { pending: number; inProgress: number; completed: number };
	@Prop({ type: Number, default: 1 }) private currentPage!: number;
	@Prop({ type: Number, default: 25 }) private itemsPerPage!: number;
	@Prop({ type: String, default: 'pickup_time' }) private sortBy!: string;
	@Prop({ type: String, default: 'desc' }) private sortDirection!: 'asc' | 'desc';
	@Prop({ type: Boolean, default: false }) private isLoading!: boolean;
	@Prop({ type: Number }) private totalCount?: number;
	@Getter('isK12Location', { namespace: 'auth' }) private isK12Location!: boolean;

	private get tableHeaders(): TableHeader[] {
		return this.isK12Location ? [
			{ text: this.$t('orders.management.table.customer_name'), name: 'customer_name', sort: this.sortBy === 'customer_name' ? this.sortDirection : 'asc' },
			{ text: this.$t('orders.management.table.status'), name: 'status', sort: this.sortBy === 'status' ? this.sortDirection : 'asc' },
			{ text: this.$t('orders.management.table.scheduled_for'), name: 'pickup_time', sort: this.sortBy === 'pickup_time' ? this.sortDirection : 'desc' },
			{ text: this.$t('orders.management.table.fulfilment_details'), name: 'fulfilment_details', sort: this.sortBy === 'fulfilment_details' ? this.sortDirection : 'asc' },
			{ text: this.$t('orders.management.table.student_name'), name: 'student_name', sort: this.sortBy === 'student_name' ? this.sortDirection : 'asc' }
		] :
			[
				{ text: this.$t('orders.management.table.customer_name'), name: 'customer_name', sort: this.sortBy === 'customer_name' ? this.sortDirection : 'asc' },
				{ text: this.$t('orders.management.table.status'), name: 'status', sort: this.sortBy === 'status' ? this.sortDirection : 'asc' },
				{ text: this.$t('orders.management.table.created_at'), name: 'created_at', sort: this.sortBy === 'created_at' ? this.sortDirection : 'desc' },
				{ text: this.$t('orders.management.table.order_type'), name: 'type', sort: this.sortBy === 'type' ? this.sortDirection : 'asc' },
				{ text: this.$t('orders.management.table.fulfilment_details'), name: 'fulfilment_details', sort: this.sortBy === 'fulfilment_details' ? this.sortDirection : 'asc' }
			];
	}

	private get totalPages(): number {
		const totalCount = this.totalCount || this.orders.length;
		return Math.ceil(totalCount / this.itemsPerPage);
	}

	private orderType(order: Order): string | undefined {
		return getOrderType(order).type;
	}

	private orderStatus(order: Order): string {
		// For K12 locations, use specific translation for cancelled status
		if (this.isK12Location && order.status === 'cancelled') {
			return this.$t('orders.management.parent_card.order_card.cancelled_k12');
		}
		return getOrderStatus(order.status);
	}

	private orderDelivery(order: Order): string | undefined {
		return getOrderDelivery(order);
	}

	private scheduledDate(order: Order): string | undefined {
		return getOrderScheduledDate(order);
	}

	private toggleSort(columnName: string): void {
		let newDirection: 'asc' | 'desc';

		if (this.sortBy === columnName) {
			// Toggle direction if same column
			newDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		}
		else {
			// Set default direction for new column
			newDirection = columnName === 'pickup_time' ? 'desc' : 'asc';
		}

		this.$emit('sort-changed', { sortBy: columnName, sortDirection: newDirection });
	}

	/**
	 * Go to previous page and trigger filter change
	 */
	private goToPreviousPage(): void {
		if (this.currentPage > 1) {
			const newPage = this.currentPage - 1;
			this.$emit('filters-changed', { ...this.tableFilters, currentPage: newPage, itemsPerPage: this.itemsPerPage });
		}
	}

	/**
	 * Go to next page and trigger filter change
	 */
	private goToNextPage(): void {
		if (this.currentPage < this.totalPages) {
			const newPage = this.currentPage + 1;
			this.$emit('filters-changed', { ...this.tableFilters, currentPage: newPage, itemsPerPage: this.itemsPerPage });
		}
	}
}
